// ===========================================================================
// ProCare.SG — public content API
// ---------------------------------------------------------------------------
// The single integration point between the public website and the backend.
// Reads come straight from Cloud Firestore (published documents only, enforced
// by security rules); the contact form goes through a Cloud Function so the
// enquiries collection stays closed to the browser.
//
// Services and Special Solutions remain static catalogue content in
// src/data/services.js — they are structural pages rather than managed
// content, and their SEO metadata is editable from the dashboard.
// ===========================================================================
import {
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    query,
    where,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { SERVICES, SPECIAL_SOLUTIONS, findService } from "@/data/services";

// --------------------------------------------------------------- tiny cache
// Content changes rarely; this keeps route changes instant without a second
// round trip, while still picking up edits within a minute.
const TTL_MS = 60_000;
const cache = new Map();

async function cached(key, loader) {
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < TTL_MS) return hit.value;
    const value = await loader();
    cache.set(key, { value, at: Date.now() });
    return value;
}

export function clearContentCache() {
    cache.clear();
}

const withId = (snapshot) => snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

/** Firestore timestamps, ISO strings and plain dates all become comparable. */
function toMillis(value) {
    if (!value) return 0;
    if (typeof value.toMillis === "function") return value.toMillis();
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}

// ------------------------------------------------------------------ Our Works
export async function getProjects() {
    return cached("projects", async () => {
        const snapshot = await getDocs(
            query(collection(db, "projects"), where("published", "==", true)),
        );
        return withId(snapshot).sort(
            (a, b) => (a.order ?? 9999) - (b.order ?? 9999) || toMillis(b.createdAt) - toMillis(a.createdAt),
        );
    });
}

export async function getProjectBySlug(slug) {
    if (!slug) return null;
    const all = await getProjects();
    const hit = all.find((project) => project.slug === slug);
    if (hit) return hit;

    // Direct lookup covers a project published between cache refreshes.
    const snapshot = await getDocs(
        query(collection(db, "projects"), where("slug", "==", slug), where("published", "==", true), limit(1)),
    );
    return snapshot.empty ? null : { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
}

export async function getProjectCategories() {
    const projects = await getProjects();
    const found = [...new Set(projects.map((p) => p.category).filter(Boolean))].sort();
    return ["All", ...found];
}

// --------------------------------------------------------------- Testimonials
export async function getTestimonials() {
    return cached("testimonials", async () => {
        const snapshot = await getDocs(
            query(collection(db, "testimonials"), where("published", "==", true)),
        );
        return withId(snapshot).sort(
            (a, b) => (a.order ?? 9999) - (b.order ?? 9999) || toMillis(b.createdAt) - toMillis(a.createdAt),
        );
    });
}

// ----------------------------------------------------------------------- Blog
export async function getBlogPosts() {
    return cached("posts", async () => {
        const snapshot = await getDocs(query(collection(db, "posts"), where("published", "==", true)));
        return withId(snapshot).sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
    });
}

export async function getBlogPostBySlug(slug) {
    if (!slug) return null;
    const all = await getBlogPosts();
    const hit = all.find((post) => post.slug === slug);
    if (hit) return hit;

    const snapshot = await getDocs(
        query(collection(db, "posts"), where("slug", "==", slug), where("published", "==", true), limit(1)),
    );
    return snapshot.empty ? null : { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
}

export async function getBlogCategories() {
    const posts = await getBlogPosts();
    const found = [...new Set(posts.map((p) => p.category).filter(Boolean))].sort();
    return ["All", ...found];
}

// ------------------------------------------------------------------- Services
export async function getServices() {
    return SERVICES;
}

export async function getSpecialSolutions() {
    return SPECIAL_SOLUTIONS;
}

export async function getServiceBySlug(slug) {
    return findService(slug) || null;
}

// --------------------------------------------------------------- Contact form
/**
 * Send an enquiry. Validation, spam limiting, storage and the client's email
 * notification all happen inside the `submitEnquiry` Cloud Function.
 * @throws {Error} with a message safe to show the visitor.
 */
export async function submitContactForm(payload) {
    const callable = httpsCallable(functions, "submitEnquiry", { timeout: 30_000 });
    try {
        const { data } = await callable({
            name: payload.name,
            mobile: payload.mobile,
            email: payload.email,
            customerType: payload.customerType,
            service: payload.service,
            propertyType: payload.propertyType,
            location: payload.location,
            date: payload.date,
            contactMethod: payload.contactMethod,
            details: payload.details,
            attachmentPath: payload.attachmentPath || "",
            consent: payload.consent === true,
            company: payload.company || "", // honeypot — always empty for humans
        });
        return data;
    } catch (error) {
        const message =
            error?.code === "functions/resource-exhausted"
                ? "You have sent several enquiries recently. Please try again later, or message us on WhatsApp."
                : error?.message || "We could not send your enquiry. Please try WhatsApp instead.";
        const wrapped = new Error(message);
        wrapped.code = error?.code;
        wrapped.details = error?.details;
        throw wrapped;
    }
}

// ------------------------------------------------------------------ Page SEO
/**
 * Per-page metadata managed in the dashboard. The `ssr` Cloud Function already
 * renders this into the HTML for crawlers; this call keeps the browser tab and
 * meta tags correct during client-side navigation.
 */
export async function getPageSEO(path) {
    if (!path) return null;
    const id = path === "/" ? "home" : path.replace(/^\//, "").replace(/\/+$/, "").replace(/\//g, "__");
    return cached(`seo:${id}`, async () => {
        try {
            const snapshot = await getDoc(doc(db, "seoPages", id));
            return snapshot.exists() ? snapshot.data() : null;
        } catch {
            return null;
        }
    });
}

// ------------------------------------------------------------- Code injection
/**
 * Injected third-party code is deliberately *not* available to the browser.
 * It is stored behind admin-only security rules and written into the served
 * HTML by the `ssr` Cloud Function, so no page ever evaluates script text it
 * received as data. Kept as a no-op for callers that still ask.
 */
export async function getCodeInjectionSettings() {
    return { header: "", body: "" };
}
